import { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import {
  useSalesDashboard, useSalesModules, useSalesSubscriptions, useSalesBillings,
  useSalesClients, useCreateModule, useUpdateModule, useDeleteModule,
  useCreateSubscription, useUpdateSubscription, useDeleteSubscription,
  useGenerateBilling,
  type SalesModule, type SalesSubscription, type SalesBilling,
} from '@/api/hooks/useSales'
import {
  ShoppingCart, Package, Users, Receipt, Plus, Pencil, Trash2,
  Copy, ExternalLink, CheckCircle2, AlertTriangle, XCircle, Clock,
  DollarSign, TrendingUp, UserCheck, UserX,
} from 'lucide-react'

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })

const ALL_PAGES = [
  { path: '/painel', label: 'Painel' },
  { path: '/chat', label: 'Chat' },
  { path: '/contatos', label: 'Contatos' },
  { path: '/broadcast', label: 'Broadcast' },
  { path: '/recuperar-comentarios', label: 'Recuperar Comentarios' },
  { path: '/whatsapp', label: 'WhatsApp' },
  { path: '/instagram', label: 'Instagram' },
  { path: '/sequencias', label: 'Sequencias' },
  { path: '/publicar', label: 'Publicar' },
  { path: '/midias', label: 'Midias' },
  { path: '/extensao', label: 'Extensao FB' },
  { path: '/download-midias', label: 'Download Midias' },
  { path: '/encurtador', label: 'Encurtador' },
  { path: '/financeiro', label: 'Financeiro' },
  { path: '/paginas', label: 'Paginas' },
  { path: '/youtube', label: 'YouTube' },
  { path: '/engagement', label: 'Engagement' },
]

const STATUS_MAP = {
  active: { label: 'Ativo', icon: CheckCircle2, cls: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30' },
  overdue: { label: 'Inadimplente', icon: AlertTriangle, cls: 'bg-red-500/15 text-red-600 border-red-500/30' },
  cancelled: { label: 'Cancelado', icon: XCircle, cls: 'bg-zinc-500/15 text-zinc-500 border-zinc-500/30' },
  trial: { label: 'Trial', icon: Clock, cls: 'bg-blue-500/15 text-blue-600 border-blue-500/30' },
}

const BILLING_STATUS = {
  pending: { label: 'Pendente', cls: 'bg-yellow-500/15 text-yellow-600 border-yellow-500/30' },
  paid: { label: 'Pago', cls: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30' },
  expired: { label: 'Expirado', cls: 'bg-red-500/15 text-red-600 border-red-500/30' },
  cancelled: { label: 'Cancelado', cls: 'bg-zinc-500/15 text-zinc-500 border-zinc-500/30' },
}

type Tab = 'modules' | 'subscriptions' | 'billings'

export function Sales() {
  const [tab, setTab] = useState<Tab>('subscriptions')
  const dashboard = useSalesDashboard()

  const d = dashboard.data

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2">
        <ShoppingCart className="h-5 w-5 text-primary" />
        <h1 className="text-xl font-bold">Vendas</h1>
      </div>

      {/* KPIs */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard icon={TrendingUp} label="MRR" value={BRL.format(d?.mrr ?? 0)} color="text-emerald-500" />
        <KpiCard icon={UserCheck} label="Clientes ativos" value={String(d?.activeClients ?? 0)} sub={`/ ${d?.totalClients ?? 0} total`} color="text-blue-500" />
        <KpiCard icon={UserX} label="Inadimplentes" value={String(d?.overdueClients ?? 0)} color="text-red-500" />
        <KpiCard icon={DollarSign} label="Recebido no mes" value={BRL.format(d?.receivedThisMonth ?? 0)} color="text-primary" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {([
          ['subscriptions', 'Assinaturas', Users],
          ['modules', 'Modulos', Package],
          ['billings', 'Cobrancas', Receipt],
        ] as const).map(([key, label, Icon]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cn(
              'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'modules' && <ModulesTab />}
      {tab === 'subscriptions' && <SubscriptionsTab />}
      {tab === 'billings' && <BillingsTab />}
    </div>
  )
}

// ── KPI Card ────────────────────────────────────────────

function KpiCard({ icon: Icon, label, value, sub, color }: { icon: React.ElementType; label: string; value: string; sub?: string; color: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={cn('h-4 w-4', color)} />
        <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  )
}

// ── Modules Tab ─────────────────────────────────────────

function ModulesTab() {
  const { data: modules = [] } = useSalesModules()
  const createModule = useCreateModule()
  const updateModule = useUpdateModule()
  const deleteModule = useDeleteModule()
  const [showForm, setShowForm] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [formName, setFormName] = useState('')
  const [formDesc, setFormDesc] = useState('')
  const [formPrice, setFormPrice] = useState('50')
  const [formPages, setFormPages] = useState<Set<string>>(new Set())

  const resetForm = () => {
    setFormName(''); setFormDesc(''); setFormPrice('50'); setFormPages(new Set())
    setShowForm(false); setEditId(null)
  }

  const startEdit = (m: SalesModule) => {
    setEditId(m.id); setFormName(m.name); setFormDesc(m.description || '')
    setFormPrice(String(m.price)); setFormPages(new Set(m.pages))
    setShowForm(true)
  }

  const handleSave = () => {
    const name = formName.trim()
    if (!name) { toast.error('Nome obrigatorio'); return }
    if (formPages.size === 0) { toast.error('Selecione pelo menos uma pagina'); return }

    const data = { name, pages: Array.from(formPages), price: parseFloat(formPrice) || 50, description: formDesc || undefined }

    if (editId) {
      updateModule.mutate({ id: editId, ...data }, { onSuccess: () => { toast.success('Modulo atualizado'); resetForm() } })
    } else {
      createModule.mutate(data, { onSuccess: () => { toast.success('Modulo criado'); resetForm() } })
    }
  }

  const togglePage = (path: string) => {
    setFormPages(prev => { const n = new Set(prev); n.has(path) ? n.delete(path) : n.add(path); return n })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{modules.length} modulos</span>
        <Button size="sm" onClick={() => { resetForm(); setShowForm(true) }} className="gap-1">
          <Plus className="h-3.5 w-3.5" /> Novo modulo
        </Button>
      </div>

      {/* Module form */}
      {showForm && (
        <div className="rounded-xl border border-primary/30 bg-card p-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Nome</label>
              <input value={formName} onChange={e => setFormName(e.target.value)} placeholder="Ex: Automacao"
                className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-primary/30" />
            </div>
            <div>
              <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Preco (R$)</label>
              <input value={formPrice} onChange={e => setFormPrice(e.target.value)} type="number" step="0.01"
                className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-primary/30" />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Descricao (opcional)</label>
            <input value={formDesc} onChange={e => setFormDesc(e.target.value)} placeholder="O que inclui..."
              className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-primary/30" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Paginas incluidas</label>
            <div className="flex flex-wrap gap-1.5">
              {ALL_PAGES.map(p => (
                <button key={p.path} onClick={() => togglePage(p.path)}
                  className={cn(
                    'rounded-md border px-2 py-1 text-[11px] font-medium transition-colors',
                    formPages.has(p.path) ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-primary/30',
                  )}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={createModule.isPending || updateModule.isPending}>
              {editId ? 'Salvar' : 'Criar'}
            </Button>
            <Button size="sm" variant="ghost" onClick={resetForm}>Cancelar</Button>
          </div>
        </div>
      )}

      {/* Module list */}
      <div className="space-y-2">
        {modules.map(m => (
          <div key={m.id} className="rounded-xl border border-border bg-card p-3 flex items-start gap-3">
            <Package className={cn('h-5 w-5 mt-0.5 shrink-0', m.active ? 'text-primary' : 'text-muted-foreground/40')} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">{m.name}</span>
                {!m.active && <Badge variant="outline" className="text-[9px]">Inativo</Badge>}
                <span className="text-xs font-bold text-primary ml-auto">{BRL.format(m.price)}</span>
              </div>
              {m.description && <p className="text-[11px] text-muted-foreground mt-0.5">{m.description}</p>}
              <div className="flex flex-wrap gap-1 mt-1.5">
                {m.pages.map(p => (
                  <Badge key={p} variant="secondary" className="text-[9px] font-normal">{ALL_PAGES.find(ap => ap.path === p)?.label || p}</Badge>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => startEdit(m)} className="rounded p-1 text-muted-foreground hover:text-foreground">
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => { if (confirm(`Deletar modulo "${m.name}"?`)) deleteModule.mutate(m.id, { onSuccess: () => toast.success('Deletado') }) }}
                className="rounded p-1 text-muted-foreground hover:text-destructive">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Subscriptions Tab ───────────────────────────────────

function SubscriptionsTab() {
  const { data: subs = [] } = useSalesSubscriptions()
  const { data: modules = [] } = useSalesModules()
  const { data: clients = [] } = useSalesClients()
  const createSub = useCreateSubscription()
  const updateSub = useUpdateSubscription()
  const deleteSub = useDeleteSubscription()
  const genBilling = useGenerateBilling()
  const [showForm, setShowForm] = useState(false)
  const [formClient, setFormClient] = useState('')
  const [formModules, setFormModules] = useState<Set<string>>(new Set())
  const [formBase, setFormBase] = useState('90')
  const [formNotes, setFormNotes] = useState('')

  const existingClientIds = useMemo(() => new Set(subs.filter(s => s.status !== 'cancelled').map(s => s.clientUserId)), [subs])
  const availableClients = clients.filter(c => !existingClientIds.has(c.id))

  const calcTotal = useMemo(() => {
    const base = parseFloat(formBase) || 90
    const modPrice = modules.filter(m => formModules.has(m.id)).reduce((s, m) => s + m.price, 0)
    return base + modPrice
  }, [formBase, formModules, modules])

  const resetForm = () => { setFormClient(''); setFormModules(new Set()); setFormBase('90'); setFormNotes(''); setShowForm(false) }

  const handleCreate = () => {
    if (!formClient) { toast.error('Selecione um cliente'); return }
    if (formModules.size === 0) { toast.error('Selecione pelo menos um modulo'); return }
    createSub.mutate({
      clientUserId: formClient,
      moduleIds: Array.from(formModules),
      basePrice: parseFloat(formBase) || 90,
      notes: formNotes || undefined,
    }, { onSuccess: () => { toast.success('Assinatura criada'); resetForm() } })
  }

  const handleGenBilling = (subId: string) => {
    genBilling.mutate(subId, {
      onSuccess: (data) => {
        const url = data.billing?.billingUrl
        if (url) {
          navigator.clipboard.writeText(url)
          toast.success('Cobranca gerada! Link copiado.')
        } else {
          toast.success('Cobranca gerada')
        }
      },
      onError: () => toast.error('Erro ao gerar cobranca'),
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{subs.length} assinaturas</span>
        <Button size="sm" onClick={() => { resetForm(); setShowForm(true) }} className="gap-1">
          <Plus className="h-3.5 w-3.5" /> Nova assinatura
        </Button>
      </div>

      {showForm && (
        <div className="rounded-xl border border-primary/30 bg-card p-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Cliente</label>
              <select value={formClient} onChange={e => setFormClient(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-primary/30">
                <option value="">Selecione...</option>
                {availableClients.map(c => (
                  <option key={c.id} value={c.id}>{c.name} ({c.username})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Preco base (R$)</label>
              <input value={formBase} onChange={e => setFormBase(e.target.value)} type="number" step="0.01"
                className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-primary/30" />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Modulos</label>
            <div className="flex flex-wrap gap-1.5">
              {modules.filter(m => m.active).map(m => (
                <button key={m.id} onClick={() => setFormModules(prev => { const n = new Set(prev); n.has(m.id) ? n.delete(m.id) : n.add(m.id); return n })}
                  className={cn(
                    'rounded-md border px-2 py-1 text-[11px] font-medium transition-colors',
                    formModules.has(m.id) ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-primary/30',
                  )}>
                  {m.name} ({BRL.format(m.price)})
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Observacoes</label>
            <input value={formNotes} onChange={e => setFormNotes(e.target.value)} placeholder="Opcional..."
              className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-primary/30" />
          </div>
          <div className="flex items-center gap-3">
            <Button size="sm" onClick={handleCreate} disabled={createSub.isPending}>Criar</Button>
            <Button size="sm" variant="ghost" onClick={resetForm}>Cancelar</Button>
            <span className="ml-auto text-sm font-bold text-primary">Total: {BRL.format(calcTotal)}</span>
          </div>
        </div>
      )}

      {/* Subscriptions list */}
      <div className="space-y-2">
        {subs.map(s => {
          const st = STATUS_MAP[s.status] || STATUS_MAP.cancelled
          const StIcon = st.icon
          return (
            <div key={s.id} className="rounded-xl border border-border bg-card p-3">
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{s.clientName}</span>
                    <span className="text-[10px] text-muted-foreground">@{s.clientUsername}</span>
                    <Badge variant="outline" className={cn('text-[9px] font-medium border', st.cls)}>
                      <StIcon className="h-2.5 w-2.5 mr-0.5" />{st.label}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {s.moduleNames.map(n => <Badge key={n} variant="secondary" className="text-[9px]">{n}</Badge>)}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                    <span>Valor: <strong className="text-foreground">{BRL.format(s.totalPrice)}</strong>/mes</span>
                    {s.nextBillingDate && <span>Proxima cobranca: {new Date(s.nextBillingDate + 'T00:00:00').toLocaleDateString('pt-BR')}</span>}
                    {s.notes && <span className="truncate max-w-[200px]">Obs: {s.notes}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={() => handleGenBilling(s.id)}
                    disabled={genBilling.isPending}>
                    <Receipt className="h-3 w-3" /> Cobrar
                  </Button>
                  {s.status !== 'cancelled' && (
                    <button onClick={() => {
                      if (confirm(`Cancelar assinatura de ${s.clientName}?`))
                        deleteSub.mutate(s.id, { onSuccess: () => toast.success('Cancelada') })
                    }} className="rounded p-1 text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )
        })}
        {subs.length === 0 && (
          <div className="text-center py-12 text-sm text-muted-foreground">Nenhuma assinatura criada</div>
        )}
      </div>
    </div>
  )
}

// ── Billings Tab ────────────────────────────────────────

function BillingsTab() {
  const [statusFilter, setStatusFilter] = useState<string>('')
  const { data: billings = [] } = useSalesBillings(statusFilter || undefined)

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url)
    toast.success('Link copiado!')
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">{billings.length} cobrancas</span>
        <div className="ml-auto flex gap-1">
          {[
            { value: '', label: 'Todas' },
            { value: 'pending', label: 'Pendentes' },
            { value: 'paid', label: 'Pagas' },
          ].map(f => (
            <button key={f.value} onClick={() => setStatusFilter(f.value)}
              className={cn(
                'rounded-md px-2.5 py-1 text-[11px] font-medium border transition-colors',
                statusFilter === f.value ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground',
              )}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {billings.map(b => {
          const st = BILLING_STATUS[b.status] || BILLING_STATUS.pending
          return (
            <div key={b.id} className="rounded-xl border border-border bg-card p-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{b.clientName}</span>
                  <Badge variant="outline" className={cn('text-[9px] font-medium border', st.cls)}>{st.label}</Badge>
                  <span className="text-sm font-bold ml-auto">{BRL.format(b.amount)}</span>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-0.5">
                  {b.dueDate && <span>Vence: {new Date(b.dueDate + 'T00:00:00').toLocaleDateString('pt-BR')}</span>}
                  {b.paidAt && <span className="text-emerald-600">Pago: {new Date(b.paidAt).toLocaleDateString('pt-BR')}</span>}
                  <span>Criado: {new Date(b.createdAt).toLocaleDateString('pt-BR')}</span>
                </div>
              </div>
              {b.billingUrl && (
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => copyUrl(b.billingUrl!)} className="rounded p-1.5 text-muted-foreground hover:text-foreground" title="Copiar link">
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                  <a href={b.billingUrl} target="_blank" rel="noopener" className="rounded p-1.5 text-muted-foreground hover:text-foreground" title="Abrir link">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              )}
            </div>
          )
        })}
        {billings.length === 0 && (
          <div className="text-center py-12 text-sm text-muted-foreground">Nenhuma cobranca encontrada</div>
        )}
      </div>
    </div>
  )
}
